import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import {
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { Subject, takeUntil } from 'rxjs';
import { AlternateIdsComponent } from 'v2/modules/shared/components/alternate-ids/alternate-ids.component';

export interface PurchaseOrderOptions {
  combineIdenticalSkus :boolean;
  alternateIds :string[];
}

@Component({
  selector: 'lum-sales-order-line-items-purchase-order-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatSelectModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    AlternateIdsComponent,
  ],
  templateUrl: './sales-order-line-items-purchase-order-dialog.component.html',
  styleUrls: ['./sales-order-line-items-purchase-order-dialog.component.scss'],
})
export class SalesOrderLineItemsPurchaseOrderDialogComponent implements OnInit, OnDestroy {
  options :PurchaseOrderOptions = {
    combineIdenticalSkus: false,
    alternateIds: [],
  };

  form: UntypedFormGroup;
  alternateIdsFormArray: UntypedFormArray;
  private onDestroy$ = new Subject();

  constructor(
    public dialogRef :MatDialogRef<SalesOrderLineItemsPurchaseOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data :{ alternateIds ?:string[] },
    private fb :UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    // Initialize with data from dialog if provided, otherwise use options
    const alternateIds = this.data?.alternateIds || this.options.alternateIds;
    
    this.form = this.fb.group({
      combineIdenticalSkus: [this.options.combineIdenticalSkus],
      alternateIds: this.fb.array([]),
    });

    this.alternateIdsFormArray = this.form.get('alternateIds') as UntypedFormArray;

    // Initialize alternateIds form array
    if (alternateIds?.length) {
      alternateIds.forEach(alternateId => {
        this.alternateIdsFormArray.push(this.fb.control(alternateId));
      });
    }

    // Subscribe to form changes to keep options in sync
    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(value => {
      this.options = {
        combineIdenticalSkus: value.combineIdenticalSkus,
        alternateIds: value.alternateIds || []
      };
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSubmit() :void {
    // Ensure we have the latest form values before closing
    const formValue = this.form.value;
    this.options = {
      combineIdenticalSkus: formValue.combineIdenticalSkus,
      alternateIds: formValue.alternateIds || []
    };
    this.dialogRef.close(this.options);
  }
}
